/**
 * The visitor's favorites as they live in localStorage under `favorites`:
 *
 *   { complexes: number[], properties: number[], listings: string[] }
 *
 * `complexes` / `properties` are own-estate ids, `listings` are MLS listing keys
 * (RESO ListingKey), newest first and capped at LISTINGS_LIMIT.
 *
 * Kept free of Vue/Vuex and of `window` so the format rules — reading an older
 * value, the cap, the read-modify-write — can be checked by a plain node script.
 */

export const STORAGE_KEY = 'favorites';
export const LISTINGS_LIMIT = 200;

// Same shape the server validates for `keys[]` on /idx/favorites/cards.
const LISTING_KEY = /^[A-Za-z0-9_-]{1,64}$/;

export function emptyFavorites() {
    return { complexes: [], properties: [], listings: [] };
}

export function isListingKey(key) {
    return typeof key === 'string' && LISTING_KEY.test(key);
}

function unique(list) {
    return list.filter((item, index) => list.indexOf(item) === index);
}

/**
 * Brings any stored value to the current shape without dropping what is in it.
 *
 * A value written before MLS favorites existed has no `listings` — it gets an
 * empty one and keeps its complexes and properties. Only a value that is not an
 * object at all (corrupt JSON, a string) falls back to empty.
 */
export function normalizeFavorites(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return emptyFavorites();
    }

    const ids = (list) => (Array.isArray(list) ? unique(list.filter((id) => id !== null && id !== undefined)) : []);

    return {
        complexes: ids(raw.complexes),
        properties: ids(raw.properties),
        listings: Array.isArray(raw.listings)
            ? unique(raw.listings.filter(isListingKey)).slice(0, LISTINGS_LIMIT)
            : [],
    };
}

export function parseFavorites(json) {
    if (typeof json !== 'string' || json === '') {
        return emptyFavorites();
    }

    try {
        return normalizeFavorites(JSON.parse(json));
    } catch (e) {
        return emptyFavorites();
    }
}

/** New key goes to the front; the oldest one falls off past the cap. */
export function addListing(favorites, key) {
    if (!isListingKey(key)) {
        return favorites;
    }

    return {
        ...favorites,
        listings: [key, ...favorites.listings.filter((item) => item !== key)].slice(0, LISTINGS_LIMIT),
    };
}

export function removeListing(favorites, key) {
    return { ...favorites, listings: favorites.listings.filter((item) => item !== key) };
}

/** Own-estate ids keep their old order: appended at the end. */
export function addEstate(favorites, kind, id) {
    if (favorites[kind].includes(id)) {
        return favorites;
    }

    return { ...favorites, [kind]: [...favorites[kind], id] };
}

export function removeEstate(favorites, kind, id) {
    return { ...favorites, [kind]: favorites[kind].filter((item) => item !== id) };
}

export function countFavorites(favorites) {
    return ['complexes', 'properties', 'listings']
        .reduce((sum, kind) => sum + (Array.isArray(favorites?.[kind]) ? favorites[kind].length : 0), 0);
}

/**
 * Storage bound to a Web Storage object (window.localStorage in the app, a stub
 * in the check script).
 *
 * `update` reads the value fresh right before writing it: another tab may have
 * changed it since this one last looked, and writing this tab's in-memory copy
 * back would silently undo that change.
 */
export function createFavoritesStorage(webStorage) {
    const read = () => {
        try {
            return parseFavorites(webStorage.getItem(STORAGE_KEY));
        } catch (e) {
            // Storage disabled (private mode, quota, policy) — favorites just stay empty.
            return emptyFavorites();
        }
    };

    const write = (favorites) => {
        const value = normalizeFavorites(favorites);

        try {
            webStorage.setItem(STORAGE_KEY, JSON.stringify(value));
        } catch (e) {
            console.error('Could not save favorites', e);
        }

        return value;
    };

    const update = (change) => write(change(read()));

    return { read, write, update };
}
